import React, { useState } from 'react';
import './TicTacToe.css';

const TicTacToe = () => {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);
  const [status, setStatus] = useState('Your turn');
  const [difficulty, setDifficulty] = useState('hard');
  const [score, setScore] = useState({ player: 0, computer: 0, draw: 0 });

  const checkWinner = (b) => {
    const lines = [
      [0,1,2], [3,4,5], [6,7,8],
      [0,3,6], [1,4,7], [2,5,8],
      [0,4,8], [2,4,6]
    ];
    for (let [a,b_,c] of lines) {
      if (b[a] && b[a] === b[b_] && b[a] === b[c]) return b[a];
    }
    if (b.every(cell => cell)) return 'Draw';
    return null;
  };

  const updateScore = (result) => {
    if (result === 'X') {
      setScore(prev => ({ ...prev, player: prev.player + 1 }));
    } else if (result === 'O') {
      setScore(prev => ({ ...prev, computer: prev.computer + 1 }));
    } else if (result === 'Draw') {
      setScore(prev => ({ ...prev, draw: prev.draw + 1 }));
    }
  };

  const minimax = (b, isMaximizing) => {
    const winner = checkWinner(b);
    if (winner === 'O') return 10;
    if (winner === 'X') return -10;
    if (winner === 'Draw') return 0;

    if (isMaximizing) {
      let best = -Infinity;
      for (let i = 0; i < 9; i++) {
        if (!b[i]) {
          b[i] = 'O';
          best = Math.max(best, minimax(b, false));
          b[i] = null;
        }
      }
      return best;
    } else {
      let best = Infinity;
      for (let i = 0; i < 9; i++) {
        if (!b[i]) {
          b[i] = 'X';
          best = Math.min(best, minimax(b, true));
          b[i] = null;
        }
      }
      return best;
    }
  };

  const findBestMove = (b) => {
    let bestScore = -Infinity;
    let move;
    for (let i = 0; i < 9; i++) {
      if (!b[i]) {
        b[i] = 'O';
        const score = minimax(b, false);
        b[i] = null;
        if (score > bestScore) {
          bestScore = score;
          move = i;
        }
      }
    }
    return move;
  };

  const computerMove = (currentBoard) => {
    const newBoard = [...currentBoard];
    let move;
    const empty = newBoard.map((v, i) => v ? null : i).filter(v => v !== null);

    if (empty.length === 0) return;

    if (difficulty === 'easy') {
      move = empty[Math.floor(Math.random() * empty.length)];
    } else if (difficulty === 'medium') {
      if (Math.random() < 0.5) {
        move = empty[Math.floor(Math.random() * empty.length)];
      } else {
        move = findBestMove(newBoard);
      }
    } else {
      move = findBestMove(newBoard);
    }

    newBoard[move] = 'O';
    setBoard(newBoard);

    const result = checkWinner(newBoard);
    if (result) {
      setStatus(result === 'Draw' ? 'It\'s a draw!' : result === 'O' ? 'Computer wins!' : 'You win!');
      updateScore(result);
      setIsPlayerTurn(false);
    } else {
      setIsPlayerTurn(true);
      setStatus('Your turn');
    }
  };

  const handleClick = (i) => {
    if (!isPlayerTurn || board[i] || checkWinner(board)) return;

    const newBoard = [...board];
    newBoard[i] = 'X';
    setBoard(newBoard);

    const result = checkWinner(newBoard);
    if (result) {
      setStatus(result === 'Draw' ? 'It\'s a draw!' : result === 'X' ? 'You win!' : 'Computer wins!');
      updateScore(result);
      setIsPlayerTurn(false);
    } else {
      setIsPlayerTurn(false);
      setStatus('Computer\'s turn...');
      setTimeout(() => computerMove(newBoard), 500);
    }
  };

  const resetGame = () => {
    setBoard(Array(9).fill(null));
    setIsPlayerTurn(true);
    setStatus('Your turn');
  };

  const renderSquare = (i) => (
    <button className={`square ${board[i] ? 'filled' : ''}`} onClick={() => handleClick(i)}>
      {board[i]}
    </button>
  );

  return (
    <div className="tic-tac-toe">
      <h2>Tic Tac Toe</h2>
      <p>{status}</p>

      <div className="scoreboard">
        <span>🙂 You: {score.player}</span>
        <span>🤖 Computer: {score.computer}</span>
        <span>🤝 Draws: {score.draw}</span>
      </div>

      <div className="difficulty">
        <label>Difficulty: </label>
        <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
      </div>

      <div className="board">
        <div className="row">
          {renderSquare(0)}{renderSquare(1)}{renderSquare(2)}
        </div>
        <div className="row">
          {renderSquare(3)}{renderSquare(4)}{renderSquare(5)}
        </div>
        <div className="row">
          {renderSquare(6)}{renderSquare(7)}{renderSquare(8)}
        </div>
      </div>

      <button className="reset-btn" onClick={resetGame}>Restart</button>
    </div>
  );
};

export default TicTacToe;
